import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from '@remix-run/react';
import { SupabaseClient } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import arrowRight from '~/assets/arrow-right.svg';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '~/components/ui/input-otp';
import { Label } from '~/components/ui/label';
import { SocialLoginButton } from '~/components/ui/social-login-button';
import useTranslation from '~/i18n/i18n';
import { useSupabase } from '~/lib/providers/supabase-provider';
import { useUserSettingsStore } from '~/lib/stores/user-settings-store';
import {
  appendConsentMetadataToUrl,
  buildLoginConsentMetadata,
  wasUserJustCreated,
  type LoginConsentMetadata,
} from '~/lib/utils/consent-metadata';
import { redirectPathnameAfterLogin } from '~/lib/utils/constants';
import { metadataMeta } from '~/lib/utils/meta-utils-client';
import { metadataLoader } from '~/lib/utils/meta-utils-server';

export const loader = metadataLoader((t) => ({
  title: t('common.routes.login.title'),
  description: t('common.routes.login.description'),
}));

export const meta = metadataMeta<typeof loader>();

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supabase = useSupabase();

  const [email, setEmail] = useState<string | null>();
  const [consentMetadata, setConsentMetadata] =
    useState<LoginConsentMetadata | null>(null);

  const redirectTo = searchParams.get('redirect') || redirectPathnameAfterLogin;

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-y-4">
      {!email && (
        <EmailForm
          supabase={supabase}
          onSuccess={(email, nextConsentMetadata) => {
            setEmail(email);
            setConsentMetadata(nextConsentMetadata);
          }}
          redirectTo={redirectTo}
        />
      )}
      {email && (
        <OTPForm
          supabase={supabase}
          email={email}
          consentMetadata={consentMetadata}
          onSuccess={() => {
            navigate(redirectTo);
          }}
        />
      )}
    </div>
  );
}

interface EmailFormProps {
  supabase: SupabaseClient;
  onSuccess: (email: string, consentMetadata: LoginConsentMetadata) => void;
  redirectTo: string;
}

const emailFormSchema = z.object({
  email: z
    .string()
    .min(1, 'auth.errors.empty')
    .email('auth.errors.invalidEmail'),
  required_consent: z.boolean().refine((value) => value, {
    message: 'auth.errors.requiredConsent',
  }),
});

const EmailForm = ({ supabase, onSuccess, redirectTo }: EmailFormProps) => {
  const { t, i18n } = useTranslation();
  const { referralCode, clearReferralCode } = useUserSettingsStore();

  const form = useForm<z.infer<typeof emailFormSchema>>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: '',
      required_consent: false,
    },
    mode: 'onChange',
  });

  const signInWithGoogle = async () => {
    const isValid = await form.trigger(['required_consent']);
    if (!isValid) return;

    const values = form.getValues();
    const consentMetadata = buildLoginConsentMetadata(values);

    // Construct redirect URL. If referral code is set, add it to the URL search
    // parameters.
    const redirectUrl = new URL('/auth/callback', window.location.href);
    if (referralCode) {
      redirectUrl.searchParams.set('referral_code', referralCode);
    }
    // Preserve the redirect parameter for after authentication
    redirectUrl.searchParams.set('redirect', redirectTo);
    appendConsentMetadataToUrl(redirectUrl, consentMetadata);

    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirectUrl.toString() },
    });
  };

  const { mutate: signIn, isLoading } = useMutation({
    mutationFn: async (data: z.infer<typeof emailFormSchema>) => {
      const consentMetadata = buildLoginConsentMetadata(data);

      const { error } = await supabase.auth.signInWithOtp({
        email: data.email,
        options: {
          data: {
            locale: i18n.language,
            referral_code: referralCode,
            ...consentMetadata,
          },
        },
      });

      if (error) {
        throw error;
      }

      return { email: data.email, consentMetadata };
    },
    onSuccess: (data) => {
      // Remove the referral code from local storage
      clearReferralCode();

      onSuccess(data.email, data.consentMetadata);
    },
    onError: (error) => {
      console.error(t('auth.errors.failedToSendOTP'), error);
      toast.error(t('auth.errors.failedToSendOTP'));
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => signIn(data))}
        className="flex w-full flex-col items-center gap-y-12">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  placeholder={t('auth.emailPlaceholder')}
                  className="text-lg"
                  type="email"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-xs">
                {t('auth.weWillSendYouOTP')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex w-full flex-col gap-4">
          <FormField
            control={form.control}
            name="required_consent"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <div className="flex items-start gap-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                      className="mt-1"
                    />
                  </FormControl>
                  <Label className="text-sm font-light leading-6">
                    {t('auth.requiredConsentPrefix')}{' '}
                    <Link
                      to="/terms-conditions"
                      className="underline hover:no-underline">
                      {t('auth.termsAndConditions')}
                    </Link>{' '}
                    {t('auth.and')}{' '}
                    <Link
                      to="/privacy-policy"
                      className="underline hover:no-underline">
                      {t('auth.privacyPolicyFormatted')}
                    </Link>
                    . {t('auth.ageConfirmation')}
                  </Label>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button
          disabled={!form.formState.isValid}
          isLoading={isLoading}
          type="submit"
          className="group flex w-full flex-row gap-4 py-5">
          {t('auth.sendCode')}
          <img
            src={arrowRight}
            alt=""
            className="transition-all duration-300 group-hover:translate-x-4 dark:invert group-hover:dark:invert-0"
          />
        </Button>

        <div className="flex w-full flex-row items-center justify-center gap-8 text-sm uppercase">
          <div className="h-px grow bg-black dark:bg-white" />
          {t('auth.or')}
          <div className="h-px grow bg-black dark:bg-white" />
        </div>

        <div className="flex w-full flex-col gap-4">
          <SocialLoginButton
            onClick={async (e) => {
              e.preventDefault();
              await signInWithGoogle();
            }}
            provider="google"
          />
        </div>
      </form>
    </Form>
  );
};

const otpFormSchema = z.object({
  token: z.string().min(1, 'auth.errors.empty'),
});

interface OTPFormProps {
  supabase: SupabaseClient;
  email: string;
  consentMetadata: LoginConsentMetadata | null;
  onSuccess: () => void;
}

const OTPForm = ({
  supabase,
  email,
  consentMetadata,
  onSuccess,
}: OTPFormProps) => {
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof otpFormSchema>>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: {
      token: '',
    },
  });

  // Function to verify the OTP
  const { mutate: verify, isLoading } = useMutation({
    mutationFn: async (otpData: { email: string; token: string }) => {
      const { data, error } = await supabase.auth.verifyOtp({
        ...otpData,
        type: 'email',
      });

      if (error) {
        throw error;
      }

      if (consentMetadata && !wasUserJustCreated(data.user)) {
        const { error: updateError } = await supabase.auth.updateUser({
          data: consentMetadata,
        });

        if (updateError) {
          throw updateError;
        }
      }

      return data;
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      console.error(t('auth.errors.wrongOTP'), error);
      toast.error(t('auth.errors.wrongOTP'));
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => verify({ email, ...data }))}
        className="flex w-full flex-col items-center space-y-8 md:w-fit">
        <FormField
          control={form.control}
          name="token"
          render={({ field }) => (
            <FormItem className="w-full">
              <Label>{t('auth.otp')}</Label>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup
                    className="flex w-full gap-4 md:w-fit"
                    inputMode="numeric">
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormDescription className="text-xs">
                {t('auth.checkYourEmail')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          disabled={!form.formState.isValid}
          isLoading={isLoading}
          type="submit"
          className="w-full">
          {t('auth.verify')}
        </Button>
      </form>
    </Form>
  );
};
